export default function PrivacyPolicyPage() {
  const lastUpdatedLabel: string = new Date().toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-8 text-3xl font-semibold text-foreground md:text-4xl">
            Politique de confidentialité (application mobile Dendromap)
          </h1>

          <div className="prose prose-slate max-w-none space-y-6 text-muted-foreground">
            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                1. Qui sommes-nous ?
              </h2>
              <p>
                La présente politique de confidentialité décrit comment{' '}
                <strong>RACINES ET CIMES</strong> (ci-après « nous ») collecte et
                traite certaines données personnelles dans le cadre de
                l&apos;utilisation de l&apos;application mobile <strong>Dendromap</strong>{' '}
                (ci-après « l&apos;Application ») et des services associés.
              </p>
              <p>
                Contact vie privée / RGPD :{' '}
                <a
                  href="mailto:contact@dendromap.fr"
                  className="text-primary hover:underline"
                >
                  contact@dendromap.fr
                </a>
                .
              </p>
              <p>
                Adresse (siège social) : 20 impasse du Moulin de la Pâtissière,
                44800 Saint-Herblain, France.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                2. Portée
              </h2>
              <p>
                Cette politique s&apos;applique à l&apos;Application et aux
                traitements nécessaires à la fourniture du service. Elle ne
                couvre pas les sites ou services tiers accessibles via des liens
                externes.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                3. Données que nous collectons
              </h2>
              <ul className="ml-4 list-disc list-inside space-y-2">
                <li>
                  <strong>Données de compte</strong> : informations nécessaires
                  à la création et à la gestion du compte (par exemple email,
                  identité professionnelle, informations de profil).
                </li>
                <li>
                  <strong>Localisation</strong> : données de localisation (si
                  vous l&apos;autorisez) afin d&apos;associer des relevés
                  terrain/inventaires à un emplacement.
                </li>
                <li>
                  <strong>Photos et médias</strong> : photos prises ou importées
                  via l&apos;Application (par exemple photos d&apos;arbres et
                  d&apos;espaces), ainsi que les métadonnées associées (selon le
                  fichier).
                </li>
                <li>
                  <strong>Données techniques</strong> : informations de
                  diagnostic et de fonctionnement (par exemple type
                  d&apos;appareil, version de l&apos;Application, journaux
                  d&apos;erreur) pour assurer la sécurité, la stabilité et
                  l&apos;amélioration du service.
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                4. Finalités et bases légales
              </h2>
              <ul className="ml-4 list-disc list-inside space-y-2">
                <li>
                  <strong>Fournir le service</strong> (exécution du contrat) :
                  création de compte, authentification, accès aux
                  fonctionnalités, synchronisation des données.
                </li>
                <li>
                  <strong>Sécurité et prévention des abus</strong> (intérêt
                  légitime) : détection de comportements anormaux, protection
                  des comptes, intégrité du service.
                </li>
                <li>
                  <strong>Support et assistance</strong> (exécution du contrat /
                  intérêt légitime) : répondre à vos demandes et résoudre les
                  incidents.
                </li>
                <li>
                  <strong>Mesure et amélioration</strong> (intérêt légitime) :
                  analyses agrégées et diagnostics pour améliorer les
                  performances et l&apos;expérience.
                </li>
              </ul>
              <p>
                Lorsque l&apos;Application demande des autorisations (par
                exemple accès à la localisation ou à la caméra), vous pouvez
                les refuser ou les retirer via les réglages de votre appareil.
                Certaines fonctionnalités peuvent alors être limitées.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                5. Partage des données et sous-traitants
              </h2>
              <p>
                Nous ne vendons pas vos données. Nous pouvons partager certaines
                données avec des prestataires techniques agissant en tant que
                sous-traitants, uniquement pour fournir et sécuriser le service.
              </p>
              <ul className="ml-4 list-disc list-inside space-y-2">
                <li>
                  <strong>Sentry</strong> : outil de monitoring et de
                  signalement d&apos;erreurs pouvant recevoir des données
                  techniques de diagnostic (par exemple événements d&apos;erreur
                  et informations sur l&apos;appareil) afin de corriger les bugs
                  et améliorer la fiabilité.
                </li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                6. Conservation
              </h2>
              <p>
                Nous conservons les données pendant la durée nécessaire aux
                finalités décrites ci-dessus, notamment pendant la durée
                d&apos;activité du compte, puis pendant une période limitée afin
                de respecter nos obligations légales et gérer d&apos;éventuels
                contentieux. Les journaux techniques sont conservés pour une
                durée proportionnée à leur finalité de sécurité et de
                diagnostic.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                7. Sécurité
              </h2>
              <p>
                Nous mettons en œuvre des mesures techniques et
                organisationnelles destinées à protéger vos données (contrôle
                des accès, sécurisation des environnements, surveillance des
                incidents). Malgré ces mesures, aucun système n&apos;est
                totalement sécurisé.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                8. Vos droits
              </h2>
              <p>
                Conformément au RGPD, vous disposez notamment des droits
                d&apos;accès, de rectification, d&apos;effacement, de limitation,
                d&apos;opposition et de portabilité (selon les cas).
              </p>
              <p>
                Pour exercer vos droits, contactez-nous à{' '}
                <a
                  href="mailto:contact@dendromap.fr"
                  className="text-primary hover:underline"
                >
                  contact@dendromap.fr
                </a>
                .
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                9. Suppression du compte et des données
              </h2>
              <p>
                Vous pouvez demander la suppression de votre compte et des
                données associées en nous contactant à{' '}
                <a
                  href="mailto:contact@dendromap.fr"
                  className="text-primary hover:underline"
                >
                  contact@dendromap.fr
                </a>
                . Nous pourrons vous demander des informations pour vérifier
                votre identité avant de traiter la demande.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                10. Enfants
              </h2>
              <p>
                L&apos;Application est destinée à un usage professionnel et
                n&apos;est pas conçue pour les enfants. Nous ne collectons pas
                sciemment de données personnelles d&apos;enfants.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="mb-4 mt-8 text-2xl font-semibold text-foreground">
                11. Mise à jour de la politique
              </h2>
              <p>
                Nous pouvons mettre à jour cette politique pour refléter des
                évolutions légales, techniques ou fonctionnelles. La version
                applicable est celle publiée sur cette page.
              </p>
            </section>

            <div className="mt-8 border-t border-border pt-6">
              <p className="text-sm text-muted-foreground">
                <strong>Dernière mise à jour :</strong> {lastUpdatedLabel}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

